import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";

const values = [
  {
    icon: "ri-flashlight-line",
    title: "及时",
    desc: "跟进影视与娱乐领域的最新动态，让资讯在第一时间抵达用户。",
  },
  {
    icon: "ri-focus-3-line",
    title: "精准",
    desc: "通过清晰的栏目划分与搜索能力，帮助用户快速找到想看的内容。",
  },
  {
    icon: "ri-book-open-line",
    title: "易读",
    desc: "重视内容层次与阅读体验，让信息获取轻松而舒适。",
  },
];

const team = [
  {
    role: "内容编辑组",
    desc: "负责影视资讯与娱乐新闻的采编整理，把关内容质量与表达方式。",
    icon: "ri-edit-2-line",
  },
  {
    role: "影视推荐组",
    desc: "从类型、年份与口碑多维度甄选影片，为用户提供可靠的观影参考。",
    icon: "ri-movie-2-line",
  },
  {
    role: "产品与体验组",
    desc: "打磨浏览、检索与分类体验，让内容门户更好用、更耐看。",
    icon: "ri-layout-4-line",
  },
];

const goals = [
  "持续丰富电影、电视剧等多类型内容，覆盖更广泛的影视娱乐场景。",
  "优化搜索与分类能力，让每一位用户都能高效找到感兴趣的内容。",
  "建设有温度的影视娱乐社区氛围，让热爱影视的人在这里相遇。",
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 红桃网",
    description:
      "了解红桃网的平台定位、团队构成与发展目标。红桃网致力于成为影视爱好者信赖的影视资讯与娱乐内容分享平台。",
    keywords: "关于红桃网,影视资讯平台,娱乐内容,平台介绍",
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 w-full">
        <div className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-6 md:py-8">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 mt-5">
              关于红桃网
            </h1>
            <p className="text-sm text-foreground-600 mt-2 max-w-2xl leading-relaxed">
              一个围绕影视资讯与娱乐内容展开的综合门户平台。
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-8 md:py-12 space-y-12">
          {/* 定位 */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium mb-4">
                平台定位
              </span>
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950 mb-4">
                影视资讯与娱乐内容的聚合分享平台
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                红桃网聚焦影视资讯与娱乐内容，涵盖电影、电视剧等多种影视类型及娱乐新闻，服务关注最新影视动态、喜欢多样化娱乐内容的广大影片爱好者与普通大众。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们希望用清晰的结构与舒适的阅读体验，把纷繁的影视娱乐信息整理成一份随时可查、随手可读的内容地图。
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: "4", label: "核心栏目" },
                { value: "30+", label: "精选内容" },
                { value: "每日", label: "资讯更新" },
                { value: "全站", label: "关键词检索" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-xl bg-background-100 border border-background-200 p-5 text-center"
                >
                  <p className="font-heading text-2xl md:text-3xl font-black text-primary-600 mb-1">
                    {s.value}
                  </p>
                  <p className="text-xs md:text-sm text-foreground-500">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* 核心价值 */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-1.5 h-7 rounded-full bg-primary-500" />
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">
                核心价值
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="rounded-xl bg-background-100 border border-background-200 p-6"
                >
                  <span className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                    <i className={`${v.icon} text-2xl text-accent-700`} />
                  </span>
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                    {v.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {v.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* 团队 */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-1.5 h-7 rounded-full bg-primary-500" />
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">
                团队介绍
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {team.map((t) => (
                <div
                  key={t.role}
                  className="rounded-xl bg-background-50 border border-background-200 p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <i className={`${t.icon} text-xl text-primary-600`} />
                    </span>
                    <h3 className="font-semibold text-foreground-900">
                      {t.role}
                    </h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* 发展目标 */}
          <section className="rounded-xl bg-background-100 border border-background-200 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-1.5 h-7 rounded-full bg-primary-500" />
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">
                发展目标
              </h2>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {goals.map((goal, index) => (
                <li
                  key={goal}
                  className="flex gap-3 text-sm text-foreground-600 leading-relaxed"
                >
                  <span className="shrink-0 w-6 h-6 rounded-full bg-primary-500 text-background-50 text-xs font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                  {goal}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}